import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { Label } from '@/components/ui/Label';
import {
  validatePlate,
  latinToArabic,
  arabicToLatin,
  digitsToEasternArabic,
  ALLOWED_LATIN_LETTERS,
} from '@/lib/plate';

interface PlateInputProps {
  digits: string;
  lettersEn: string;
  lettersAr: string;
  onChange: (next: { digits: string; lettersEn: string; lettersAr: string }) => void;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Saudi plate input. Two coupled fields:
 *   [4 digits] [3 EN letters]   ⇆   [4 digits] [3 AR letters]
 *
 * Behavior:
 *   - As user types EN letters, AR auto-fills using official mapping
 *   - As user types AR letters, EN auto-fills
 *   - Invalid letters get visual rejection (red ring) and error message
 *   - Banned combos (SEX, USA, etc.) get rejected
 *   - Field reads LTR for digits + EN, RTL for AR
 */
export function PlateInput({
  digits,
  lettersEn,
  lettersAr,
  onChange,
  disabled = false,
  required = false,
}: PlateInputProps) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState({ digits: false, lettersEn: false, lettersAr: false });

  const validation = validatePlate({ digits, lettersEn, lettersAr });

  // Sanitize digits: numbers only, max 4
  const handleDigits = (raw: string) => {
    const cleaned = raw.replace(/[^0-9]/g, '').slice(0, 4);
    onChange({ digits: cleaned, lettersEn, lettersAr });
  };

  // EN letters: uppercase letters only, max 3, auto-derive AR
  const handleEn = (raw: string) => {
    const cleaned = raw.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    const derivedAr = latinToArabic(cleaned);
    onChange({ digits, lettersEn: cleaned, lettersAr: derivedAr });
  };

  // AR letters: only allowed Arabic, max 3, auto-derive EN
  const handleAr = (raw: string) => {
    // Strip whitespace
    const trimmed = raw.replace(/\s/g, '').slice(0, 3);
    const derivedEn = arabicToLatin(trimmed);
    onChange({ digits, lettersEn: derivedEn, lettersAr: trimmed });
  };

  // Compose visual styles based on validation state
  const fieldClass = (valid: boolean, hasInteraction: boolean) =>
    `flex h-11 rounded-md border bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 transition-colors ${
      hasInteraction && !valid
        ? 'border-destructive focus-visible:ring-destructive'
        : 'border-input focus-visible:ring-ring'
    }`;

  const isOverallValid = validation.valid;

  return (
    <div className="space-y-3">
      {/* Header with overall status */}
      <div className="flex items-center justify-between">
        <Label>
          {t('plate.label')} {required && <span className="text-destructive">*</span>}
        </Label>
        {touched.lettersEn && (
          isOverallValid ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-3 w-3" /> {t('plate.valid')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-destructive">
              <AlertCircle className="h-3 w-3" /> {t('plate.invalid')}
            </span>
          )
        )}
      </div>

      {/* English plate row */}
      <div className="space-y-1.5">
        <div className="flex gap-2">
          <input
            type="text"
            dir="ltr"
            inputMode="numeric"
            placeholder="3675"
            value={digits}
            onChange={(e) => handleDigits(e.target.value)}
            onBlur={() => setTouched((s) => ({ ...s, digits: true }))}
            disabled={disabled}
            className={`${fieldClass(validation.digits.valid, touched.digits)} font-mono text-center w-24 text-lg tracking-widest`}
            aria-label={t('plate.digits')}
            maxLength={4}
          />
          <input
            type="text"
            dir="ltr"
            placeholder="KRA"
            value={lettersEn}
            onChange={(e) => handleEn(e.target.value)}
            onBlur={() => setTouched((s) => ({ ...s, lettersEn: true }))}
            disabled={disabled}
            className={`${fieldClass(validation.lettersEn.valid, touched.lettersEn)} font-mono text-center flex-1 text-lg tracking-widest uppercase`}
            aria-label={t('plate.lettersEn')}
            maxLength={3}
          />
        </div>
        {touched.lettersEn && !validation.lettersEn.valid && validation.lettersEn.error && (
          <p className="text-xs text-destructive flex items-start gap-1">
            <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
            {validation.lettersEn.error}
          </p>
        )}
      </div>

      {/* Arabic plate row (auto-filled, but editable) */}
      <div className="space-y-1.5">
        <div className="flex gap-2" dir="rtl">
          <input
            type="text"
            dir="rtl"
            placeholder="٣٦٧٥"
            value={digits ? digitsToEasternArabic(digits) : ''}
            readOnly
            disabled={disabled}
            className="flex h-11 rounded-md border border-input bg-muted/30 px-3 py-2 text-lg font-mono text-center tracking-widest w-24 text-muted-foreground"
            aria-label={t('plate.digitsAr')}
          />
          <input
            type="text"
            dir="rtl"
            placeholder="كرا"
            value={lettersAr}
            onChange={(e) => handleAr(e.target.value)}
            onBlur={() => setTouched((s) => ({ ...s, lettersAr: true }))}
            disabled={disabled}
            className={`${fieldClass(validation.lettersAr.valid, touched.lettersAr)} font-arabic text-center flex-1 text-lg tracking-widest`}
            style={{ fontFamily: '"Noto Naskh Arabic", "Tahoma", sans-serif' }}
            aria-label={t('plate.lettersAr')}
            maxLength={3}
          />
        </div>
        {touched.lettersAr && !validation.lettersAr.valid && validation.lettersAr.error && (
          <p className="text-xs text-destructive flex items-start gap-1" dir="rtl">
            <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
            {validation.lettersAr.error}
          </p>
        )}
      </div>

      {/* Helper text showing allowed letters */}
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        {t('plate.allowedHelp')}: <span className="font-mono">{ALLOWED_LATIN_LETTERS.join(' ')}</span>
      </p>
    </div>
  );
}
